import type { ReelItemProperty } from '~/components/PropertyReelsFeed/types';

import {
	buildShortsOgImagePath,
	parseShortsThumbUrl,
	SHORTS_OG_IMAGE_HEIGHT,
	SHORTS_OG_IMAGE_TYPE,
	SHORTS_OG_IMAGE_WIDTH
} from '#shared/utils/shortsOgImage';

import { applyShortsSeoMeta } from './shortsSharedSeoMeta';

export function resolveShortsOgImageUrl(
	property: ReelItemProperty | undefined
): string | undefined {
	if (!property) return undefined;
	return property.featuredVideos?.[0]?.thumbnail ?? undefined;
}

interface ShortsShareImage {
	url: string;
	width?: number;
	height?: number;
	type?: typeof SHORTS_OG_IMAGE_TYPE;
}

export function resolveShortsShareImage(
	property: ReelItemProperty | undefined
): ShortsShareImage | undefined {
	const thumbnail = resolveShortsOgImageUrl(property);
	if (!thumbnail) return undefined;

	const thumbRef = parseShortsThumbUrl(thumbnail);
	if (!thumbRef) return { url: thumbnail };

	return {
		url: buildShortsOgImagePath(thumbRef),
		width: SHORTS_OG_IMAGE_WIDTH,
		height: SHORTS_OG_IMAGE_HEIGHT,
		type: SHORTS_OG_IMAGE_TYPE
	};
}

function toPositiveInteger(value: number | null | undefined): number | null {
	if (!value || value <= 0) return null;
	return Math.floor(value);
}

function capitalizeFirstLetter(value: string | undefined): string {
	if (!value) return 'Imóvel';
	return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatCount(
	count: number,
	singularLabel: string,
	pluralLabel: string
): string {
	return `${count} ${count === 1 ? singularLabel : pluralLabel}`;
}

function joinWithAnd(parts: string[]): string {
	if (parts.length <= 1) return parts[0] ?? '';
	if (parts.length === 2) return `${parts[0]} e ${parts[1]}`;
	return `${parts.slice(0, -1).join(', ')} e ${parts[parts.length - 1]}`;
}

function formatAskingPrice(value: number | null | undefined): string {
	if (!value || value <= 0) return '';
	return new Intl.NumberFormat('pt-BR', {
		style: 'currency',
		currency: 'BRL',
		maximumFractionDigits: 0
	}).format(value);
}

function buildTitle(property: ReelItemProperty): string {
	const propertyType = capitalizeFirstLetter(property.propertyType?.name);
	const suites = toPositiveInteger(property.suites);
	const bedrooms = toPositiveInteger(property.bedrooms);
	const roomPart = suites
		? ` com ${formatCount(suites, 'suíte', 'suítes')}`
		: bedrooms
			? ` com ${formatCount(bedrooms, 'quarto', 'quartos')}`
			: '';

	const location = [property.region, property.city].filter(Boolean).join(', ');
	const formattedPrice = formatAskingPrice(property.askingPrice);

	if (!location || !formattedPrice) {
		return `${propertyType}${roomPart} | Shorts | PilarHomes`;
	}

	return `${propertyType}${roomPart} à venda em ${location} por ${formattedPrice} | Shorts | PilarHomes`;
}

function buildDescription(property: ReelItemProperty): string {
	const propertyType = capitalizeFirstLetter(property.propertyType?.name);
	const region = property.region || property.city || 'sua região';
	const area = toPositiveInteger(property.area);
	const bedrooms = toPositiveInteger(property.bedrooms);
	const suites = toPositiveInteger(property.suites);
	const parkingSpots = toPositiveInteger(property.parkingSpots);

	const features: string[] = [];
	if (area) features.push(`${area} m²`);
	if (bedrooms) features.push(formatCount(bedrooms, 'quarto', 'quartos'));
	if (suites) features.push(formatCount(suites, 'suíte', 'suítes'));
	if (parkingSpots) features.push(formatCount(parkingSpots, 'vaga', 'vagas'));

	const detailsPart =
		features.length > 0 ? ` com ${joinWithAnd(features)}` : '';

	const endingSentence = property.isExclusive
		? 'Descubra todos os detalhes deste imóvel exclusivo da PilarHomes!'
		: 'Descubra todos os detalhes deste imóvel no PilarHomes!';

	return `Seu novo lar em ${region} te espera: ${propertyType} à venda${detailsPart}. ${endingSentence}`;
}

function buildKeywords(property: ReelItemProperty): string {
	const propertyType = capitalizeFirstLetter(property.propertyType?.name);
	return [propertyType, property.region, property.city, 'shorts', 'PilarHomes']
		.filter(Boolean)
		.join(', ');
}

export function useShortsFeedSeo(
	currentProperty: ComputedRef<ReelItemProperty | undefined>,
	fallbackProperty?: Ref<ReelItemProperty | undefined>
) {
	const seoProperty = computed(
		() => currentProperty.value ?? fallbackProperty?.value
	);

	const seoTitle = computed(() => {
		const property = seoProperty.value;
		if (!property) return 'Shorts de Imóveis | PilarHomes';
		return buildTitle(property);
	});

	const seoDescription = computed(() => {
		const property = seoProperty.value;
		if (!property) {
			return 'Descubra todos os detalhes deste imóvel no PilarHomes!';
		}
		return buildDescription(property);
	});

	const seoKeywords = computed(() => {
		const property = seoProperty.value;
		if (!property) return 'PilarHomes, imóveis, shorts';
		return buildKeywords(property);
	});

	const shareImage = computed(() => resolveShortsShareImage(seoProperty.value));

	applyShortsSeoMeta({
		title: () => seoTitle.value,
		description: () => seoDescription.value,
		keywords: () => seoKeywords.value,
		ogImage: () => shareImage.value?.url,
		ogImageWidth: () => shareImage.value?.width,
		ogImageHeight: () => shareImage.value?.height,
		ogImageType: () => shareImage.value?.type
	});
}
